import {
  leadsImportListQueryKey,
  leadsImportPartialUpdateMutation,
  leadsImportPreviewRetrieveOptions,
  leadsImportRetrieveOptions,
  StepEnum
} from '@crm/api'
import { useImportLeads } from '@crm/features/leads/contexts'
import { useImportFieldMapping } from '@crm/features/leads/hooks'
import { useStyles } from '@crm/features/leads/styles'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useIdParam } from '@crm/hooks/useIdParam'
import { paths } from '@crm/routes'
import { TableContainer, Text } from '@shared/components'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Col, Form, Row, Select, TableColumnType } from 'antd'
import { useEffect, useMemo } from 'react'

export type TableRecord = {
  key: number
  header_row: string
  module_field: number
  row: string
}

export type ColumnConfig = {
  title: string
  dataIndex: string
  key: string
  render?: (value: unknown, record: TableRecord) => React.ReactNode
} & TableColumnType

export const ConfirmField = () => {
  const { t } = useLocales()
  const { id } = useIdParam()
  const [form] = Form.useForm()
  const navigate = useAppNavigate()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { setIsPending } = useImportLeads()
  const { styles } = useStyles()

  const { leadOptions, contactOptions, transform, isLoading } =
    useImportFieldMapping()

  const { data: preview } = useQuery({
    ...leadsImportPreviewRetrieveOptions({ path: { id: id } })
  })

  const { data: leadImport } = useQuery({
    ...leadsImportRetrieveOptions({ path: { id: id } }),
    enabled: !!id,
    refetchOnMount: 'always'
  })

  useEffect(() => {
    if (leadImport?.headers) {
      const leadValues: Record<number, string> = {}
      const contactValues: Record<number, string> = {}

      leadImport?.headers?.lead_headers?.forEach((header) => {
        if (header?.key) leadValues[header.column_index] = header.key
      })

      leadImport?.headers?.contact_headers?.forEach((header) => {
        if (header.key) contactValues[header.column_index] = header.key
      })

      form.setFieldsValue({
        lead_headers: leadValues,
        contact_headers: contactValues
      })
    }
  }, [leadImport])

  const { mutate: updateMutation } = useMutation({
    ...leadsImportPartialUpdateMutation(),
    onMutate: () => setIsPending(true),
    onSuccess: (response) => {
      invalidateQueries([leadsImportListQueryKey()])
      navigate(
        paths.leadImport.item(
          response.id.toString(),
          response.step?.toString() || ''
        )
      )
    },
    onError: (error) => showApiErrors(error, form),
    onSettled: () => setIsPending(false)
  })

  const tableData = useMemo(() => {
    if (!Array.isArray(preview?.data) || preview.data.length < 2) {
      return []
    }

    const [headersRow, rawRow] = preview.data

    if (!Array.isArray(headersRow)) {
      return []
    }

    return headersRow.map((header, index) => ({
      key: index,
      header_row: header,
      module_field: index,
      row: Array.isArray(rawRow) ? rawRow[index] ?? '' : ''
    }))
  }, [preview])

  const leadTableColumns: ColumnConfig[] = [
    {
      title: t('import.header_row'),
      dataIndex: 'header_row',
      key: 'header_row',
      width: 300,
      render: (_, record) => (
        <Text className={styles.customText} isEllipsis>
          {record.header_row}
        </Text>
      )
    },
    {
      title: t('import.module_field'),
      dataIndex: 'module_field',
      key: 'module_field',
      render: (_, record) => (
        <Form.Item name={['lead_headers', record.key]} noStyle>
          <Select
            options={leadOptions}
            placeholder={t('main.select')}
            className="w-full"
            allowClear
          />
        </Form.Item>
      )
    },
    {
      title: t('import.raw1'),
      dataIndex: 'row',
      key: 'row',
      width: 200,
      render: (_, record) => (
        <Text className={styles.customText} isEllipsis>
          {record.row}
        </Text>
      )
    }
  ]

  const contactTableColumns: ColumnConfig[] = [
    {
      title: t('import.header_row'),
      dataIndex: 'header_row',
      key: 'header_row',
      width: 300,
      render: (_, record) => (
        <Text className={styles.customText} isEllipsis>
          {record.header_row}
        </Text>
      )
    },
    {
      title: t('import.module_field'),
      dataIndex: 'module_field',
      key: 'module_field',
      render: (_, record) => (
        <Form.Item name={['contact_headers', record.key]} noStyle>
          <Select
            className="w-full"
            options={contactOptions}
            placeholder={t('main.select')}
            allowClear
          />
        </Form.Item>
      )
    },
    {
      title: t('import.raw1'),
      dataIndex: 'row',
      key: 'row',
      width: 200,
      render: (_, record) => (
        <Text className={styles.customText} isEllipsis>
          {record.row}
        </Text>
      )
    }
  ]

  const onSubmit = async () => {
    const values = await form.validateFields()

    const leadHeadersPayload = transform(values.lead_headers, 'lead')
    const contactHeadersPayload = transform(values.contact_headers, 'contact')

    updateMutation({
      path: { id: id },
      body: {
        headers: {
          lead_headers: leadHeadersPayload,
          contact_headers: contactHeadersPayload
        },
        step: StepEnum.CONFIRM_FIELD_MAPPING
      }
    })
  }

  return (
    <>
      <Text weight={500} size={18}>
        {t('import.description_for_confirm_field_mapping')}
      </Text>

      <Form
        name={`form-${StepEnum.CONFIRM_FIELD_MAPPING}`}
        form={form}
        onFinish={onSubmit}
        className="mt-20"
      >
        <Row gutter={16}>
          <Col span={12}>
            <TableContainer
              rowKey="key"
              columns={leadTableColumns}
              isLoading={isLoading}
              leftChildren={<Text weight={600}>{t('import.lead_fields')}</Text>}
              showSearch={false}
              data={{ results: tableData }}
              pagination={false}
              bordered
            />
          </Col>

          <Col span={12}>
            <TableContainer
              rowKey="key"
              isLoading={isLoading}
              columns={contactTableColumns}
              leftChildren={
                <Text weight={600}>{t('import.contact_fields')}</Text>
              }
              showSearch={false}
              data={{ results: tableData }}
              pagination={false}
              bordered
            />
          </Col>
        </Row>
      </Form>
    </>
  )
}
